'use client';

import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { setupCollapse } from '../home/effects/setup-collapse';
import { setupDataWidths } from '../home/effects/setup-data-widths';
import { setupHomeMotion } from '../home/effects/setup-home-motion';
import { setupHomeNavigation } from '../home/effects/setup-navigation';
import { setupHomeSliders } from '../home/effects/setup-home-sliders';
import { setupPortfolioFilter } from '../home/effects/setup-portfolio-filter';

/**
 * Motion for the inner landing pages. The source design wraps every page, not only the
 * home page, in `#smooth-wrapper`/`#smooth-content` and runs ScrollSmoother over it, so
 * this mirrors HomeEffects: the same smoothing values, the `effects: true` that drives the
 * `data-speed` parallax figures, and the smoother handed to the navigation helper so
 * back-to-top and anchor jumps go through it instead of fighting it.
 */
export function LandingPageEffects() {
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.light-ship-smart-root');
    const wrapper = root?.querySelector<HTMLElement>('#smooth-wrapper');
    const content = root?.querySelector<HTMLElement>('#smooth-content');
    if (!root || !wrapper || !content) return;

    gsap.registerPlugin(
      ScrollTrigger,
      ScrollSmoother,
      ScrollToPlugin,
      SplitText
    );

    ScrollSmoother.get()?.kill();
    // Apply source-design widths before GSAP performs any geometry reads.
    const cleanupDataWidths = setupDataWidths(root);

    // Touch devices scroll natively; the transform-based smoother fights their own
    // momentum, which is why the home page skips it below this breakpoint as well.
    const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
    const smoother = isMobile
      ? null
      : ScrollSmoother.create({
          wrapper,
          content,
          smooth: 1.35,
          effects: true,
          smoothTouch: 0.15,
          ignoreMobileResize: true,
        });

    const cleanups = [
      cleanupDataWidths,
      setupCollapse(root),
      setupHomeNavigation(root, smoother),
      setupHomeSliders(root),
      setupHomeMotion(root),
      setupPortfolioFilter(root),
    ];
    let refreshFrame: number | undefined;
    const settleFrame = window.requestAnimationFrame(() => {
      refreshFrame = window.requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });
    });

    return () => {
      window.cancelAnimationFrame(settleFrame);
      if (refreshFrame !== undefined) {
        window.cancelAnimationFrame(refreshFrame);
      }
      cleanups.reverse().forEach((cleanup) => cleanup());
      smoother?.kill();
      ScrollTrigger.clearScrollMemory();
    };
  }, []);

  return null;
}
